import React, { useState } from 'react';
import { Upload, X } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import Avatar from './Avatar';
import supabase from '../lib/supabase';

interface AvatarUploadProps {
  onUploadComplete?: (url: string) => void;
  className?: string;
  initialImage?: string | null;
}

const AvatarUploadProfile: React.FC<AvatarUploadProps> = ({
  onUploadComplete,
  className = '',
  initialImage,
}) => {
  const { user } = useAuthStore();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to check if URL is from Supabase storage
  const isSupabaseStorageUrl = (url: string): boolean => {
    return url.includes('supabase') && url.includes('/storage/');
  };

  // Only add cache busting to Supabase URLs, not external OAuth URLs
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialImage ? (
      isSupabaseStorageUrl(initialImage) 
        ? `${initialImage}?v=${Date.now()}` // Cache bust Supabase URLs
        : initialImage // Keep external URLs (Facebook, Spotify, etc.) unchanged
    ) : null
  );

  const getStoragePathFromPublicUrl = (url: string) => {
    const split = url.split('/object/public/');
    return split.length === 2 ? split[1] : null;
  };

  // Get the current user's profile ID for privacy
  const getCurrentUserProfileId = async (): Promise<string | null> => {
    try {
      const { data: profile, error: profileError } = await supabase
        .rpc('get_current_user_profile');
      
      if (profileError || !profile || profile.length === 0) {
        console.error('Failed to get user profile:', profileError);
        return null;
      }
      
      return profile[0].id;
    } catch (error) {
      console.error('Error fetching profile ID:', error);
      return null;
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('🔍 DEBUG - File type:', file.type);
    console.log('🔍 DEBUG - File name:', file.name);
    console.log('🔍 DEBUG - File size:', file.size);

    try {
      setIsUploading(true);
      setError(null);

      const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
      const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

      if (file.size > MAX_FILE_SIZE) {
        throw new Error('File too large. Maximum size is 5MB.');
      }

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        throw new Error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
      }

      // If user is not logged in, just preview locally
      if (!user?.id) {
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);
        onUploadComplete?.(objectUrl);
        return;
      }

      // Get the profile ID for privacy-compliant folder structure
      const profileId = await getCurrentUserProfileId();
      console.log('🔍 DEBUG - Profile ID from get_current_user_profile:', profileId);
      
      if (!profileId) {
        throw new Error('Unable to get user profile. Please try again.');
      }

      // Also test the other RPC function for comparison
      try {
        const { data: directId } = await supabase.rpc('get_current_user_profile_id');
        console.log('🔍 DEBUG - Profile ID from get_current_user_profile_id:', directId);
        console.log('🔍 DEBUG - IDs match:', profileId === directId);
      } catch (err) {
        console.log('🔍 DEBUG - Error calling get_current_user_profile_id:', err);
      }

      const fileExt = file.name.split('.').pop();
      const filePath = `${profileId}/avatar.${fileExt}`;
      console.log('🔍 DEBUG - Upload file path:', filePath);

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Only add cache busting to Supabase URLs
      const cacheBustedUrl = isSupabaseStorageUrl(publicUrl) 
        ? `${publicUrl}?v=${Date.now()}`
        : publicUrl;

      setPreviewUrl(cacheBustedUrl);
      onUploadComplete?.(cacheBustedUrl);
    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error.message || 'Failed to upload image');
      // Reset to original image on error
      setPreviewUrl(initialImage ? (
        isSupabaseStorageUrl(initialImage) 
          ? `${initialImage}?v=${Date.now()}` 
          : initialImage
      ) : null);
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = async () => {
    try {
      setError(null);

      // Only try to remove from storage if it's a Supabase URL
      if (previewUrl && isSupabaseStorageUrl(previewUrl)) {
        const filePath = getStoragePathFromPublicUrl(previewUrl);

        if (!filePath) {
          setError('Could not extract file path from avatar URL.');
          return;
        }

        const { error: storageError } = await supabase.storage
          .from('avatars')
          .remove([filePath]);

        if (storageError) {
          console.error('Error removing avatar from storage:', storageError);
          setError('Failed to remove image from storage');
          return;
        }
      }

      setPreviewUrl(null);
      onUploadComplete?.('');
    } catch (error: any) {
      console.error('Error removing avatar:', error);
      setError(error.message || 'Failed to remove image');
    }
  };

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {previewUrl ? (
        <div className="relative flex items-center justify-center">
          <Avatar
            src={previewUrl}
            alt="Profile preview"
            size="xl"
            loading={isUploading}
          />
          <button
            type="button"
            onClick={removeImage}
            disabled={isUploading}
            className="absolute -top-2 -right-2 bg-surface-light text-neutral border-4 border-neutral rounded-full p-1 hover:bg-base transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <label className="relative cursor-pointer">
            <div className="w-24 h-24 rounded-full bg-primary-dark bg-opacity-50 ring-2 ring-surface-light border-2 border-surface-light transition-all flex items-center justify-center ">
              <div className="flex flex-col items-center justify-center text-center p-2">
                <Upload className="w-6 h-6 text-surface-light mb-1" />
                <span className="text-[10px] leading-tight text-surface-light">
                  {isUploading ? 'Uploading...' : 'Upload your profile picture'}
                </span>
              </div>
            </div>
            <input
              type="file"
              className="hidden"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageChange}
              disabled={isUploading}
            />
          </label>
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-primary text-center">{error}</p>
      )}
    </div>
  );
};

export default AvatarUploadProfile;